import React, { useMemo, useRef, useState } from 'react';
import * as BizCharts from 'bizcharts';
import useMountEffect from '../../hooks/useMountEffect';
import './chartTheme';
import { withNiceScale } from './chartUtils';
import { computeCategoryBreakdownChartHeight } from '../analytics_charts';
import { SpendSourceDescriptor } from '../../utils/AnalyticsUtils';

const { Chart, Coordinate, Axis, Geom, Legend, Tooltip } = BizCharts;
const AXIS_LINE_COLOR = '#D9D9D9';

// Long category names (e.g. "Inside - HVAC - Exhaust Fans") overflow the left axis gutter and get
// clipped. Truncate to fit, matching the shared CategoryBreakdownBarChart; the table shows the full
// name. Keep this in sync with the chart's left padding below.
const CATEGORY_LABEL_MAX_CHARS = 24;
const truncateCategoryLabel = (raw: any): string => {
	const text = raw === null || raw === undefined ? '' : String(raw);
	if (text.length <= CATEGORY_LABEL_MAX_CHARS) {
		return text;
	}
	return text.slice(0, CATEGORY_LABEL_MAX_CHARS - 1).trimEnd() + '…';
};

// Horizontal bar per category, stacked by spend source (OPE-4875). Hovering a bar shows each source's
// value plus the category total. Body-only (no card/title/download) — it's rendered as the single-mode
// view inside AnalyticsBreakdownChartSection, which provides the card, comparison toggle, and download.
export type SpendBySourceStackedBarsProps = {
	// SpendCostsByField rows (string amounts), one per category.
	rows: any[];
	activeSources: SpendSourceDescriptor[];
	currency: any;
	height?: number;
};

export const SpendBySourceStackedBars = ({
	rows,
	activeSources,
	currency,
	height = 360,
}: SpendBySourceStackedBarsProps) => {
	const formatValue = (val) =>
		val > 0 ? currency.symbolFormat(val, 0) : currency.symbolFormat(0, 0);

	// Fold each row into one record per active source. Bars are keyed by a per-row `category` rather
	// than the raw fieldName: distinct rows that share a display name (e.g. two locations both named
	// "Main St") get a " (2)" suffix so they render as separate bars matching the id-keyed table,
	// instead of BizCharts merging them into one summed bar.
	const { foldedData, categoryOrder } = useMemo(() => {
		const folded: any[] = [];
		const nameCounts: { [name: string]: number } = {};
		const rowTotals = (rows || []).map((row) => {
			const base =
				row.fieldName === null || row.fieldName === undefined ? '' : String(row.fieldName);
			const seen = nameCounts[base] || 0;
			nameCounts[base] = seen + 1;
			const category = seen === 0 ? base : `${base} (${seen + 1})`;
			let categoryTotal = 0;
			activeSources.forEach((source) => {
				const value = parseFloat(row[source.key]) || 0;
				categoryTotal += value;
				folded.push({ category, source: source.label, value });
			});
			return { category, categoryTotal };
		});
		// Ascending so the largest category renders at the top once the coordinate is transposed.
		const order = rowTotals
			.slice()
			.sort((rowA, rowB) => rowA.categoryTotal - rowB.categoryTotal)
			.map((row) => row.category);
		return { foldedData: folded, categoryOrder: order };
	}, [rows, activeSources]);

	const scale = useMemo(
		() =>
			withNiceScale({
				value: { type: 'linear', min: 0, nice: true, alias: 'Spend' },
				category: { type: 'cat', values: categoryOrder },
				source: { type: 'cat', values: activeSources.map((source) => source.label) },
			}),
		[categoryOrder, activeSources]
	);

	// Size from the actual (post-dedupe) bar count, not the caller's height — the section derives its
	// height from un-suffixed fieldNames, which undercounts when duplicate display names are split into
	// separate bars here. Never shrink below the provided height.
	const chartHeight = Math.max(
		height,
		computeCategoryBreakdownChartHeight(categoryOrder.length, false)
	);

	// Append a Total row to the shared tooltip so hover shows the breakup and the total together.
	// Sum the hovered bar's own segments rather than a name-keyed lookup, so categories that happen to
	// share a display name can't mis-total.
	const customItems = (items) => {
		if (!items || items.length === 0) {
			return items;
		}
		const total = items.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
		const formatted = items.map((item) => ({
			...item,
			value: formatValue(parseFloat(item.value) || 0),
		}));
		formatted.push({
			name: 'Total',
			value: formatValue(total),
			color: 'rgba(0,0,0,0.85)',
			marker: false,
		});
		return formatted;
	};

	// Drive width explicitly (autoFit off) so transposed bars and labels stay aligned — mirrors the
	// approach in CategoryBreakdownBarChart.
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);
	useMountEffect(() => {
		const container = wrapperRef.current;
		if (!container) return undefined;
		const measure = () => {
			const width = container.offsetWidth;
			if (width > 0) setContainerWidth(width);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(container);
		return () => observer.disconnect();
	});

	return (
		<div
			ref={wrapperRef}
			style={{ height: chartHeight, minHeight: chartHeight, maxHeight: chartHeight }}
		>
			{containerWidth && foldedData.length > 0 ? (
				<Chart
					width={containerWidth}
					height={chartHeight}
					padding={[20, 40, 60, 160]}
					data={foldedData}
					scale={scale}
					autoFit={false}
					theme="openwrench"
				>
					<Coordinate transpose={true} />
					<Axis
						name="category"
						label={{
							formatter: truncateCategoryLabel,
							style: { fontSize: 12, fontFamily: 'Roboto', fill: 'rgba(0,0,0,0.45)' },
						}}
					/>
					<Axis
						name="value"
						label={{
							formatter: formatValue,
							style: { fontSize: 12, fontFamily: 'Roboto', fill: 'rgba(0,0,0,0.45)' },
						}}
						grid={{ line: { style: { stroke: AXIS_LINE_COLOR, lineDash: [4, 4] } } }}
					/>
					<Legend
						position="bottom"
						itemName={{ style: { fontSize: 12, fontFamily: 'Roboto', fill: 'rgba(0,0,0,0.65)' } }}
						marker={{ symbol: 'square' }}
					/>
					<Tooltip shared={true} showTitle={true} showMarkers={false} customItems={customItems} />
					<Geom
						type="interval"
						position="category*value"
						color="source"
						adjust="stack"
						style={{ stroke: '#fff', lineWidth: 1 }}
					/>
				</Chart>
			) : null}
		</div>
	);
};
